import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Check, Navigation, MapPin, Flag, Ban, PauseCircle, CalendarClock, Smartphone, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import { deriveTechProgress, enRouteEta, techProgressSteps, type TechProgressSource } from '@/utils/techProgress';

interface TechProgressCardProps {
  job: TechProgressSource & { techs?: { name: string } | null };
}

const STEP_ICON = {
  accepted: Check,
  en_route: Navigation,
  on_site: MapPin,
  finished: Flag,
} as const;

/**
 * Four-step stepper (Accepted → En route → On site → Finished) fed by the
 * columns the Steddy app writes, plus the decline / hold / reschedule states
 * the office has to act on.
 */
export function TechProgressCard({ job }: TechProgressCardProps) {
  const { locale, timezone } = useCompanyLocale();
  if (!job.assigned_tech_id || job.status === 'cancelled') return null;

  const progress = deriveTechProgress(job);
  const steps = techProgressSteps(job);
  const stamp = (value: string | null) =>
    value ? formatDocDate(value, `${locale.shortDate}, h:mm a`, timezone) : null;
  const techName = job.techs?.name ?? 'The technician';
  const eta = enRouteEta(job);
  const etaStamp = (value: string) => formatDocDate(value, 'h:mm a', timezone);

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-info/10 flex items-center justify-center">
              <Smartphone className="h-4 w-4 text-info" />
            </div>
            <CardTitle className="text-lg">Technician progress</CardTitle>
          </div>
          {progress && (
            <Badge variant={progress.variant}>{progress.stage === 'rescheduled' ? 'Asked again' : progress.label}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {steps.map((step, i) => {
            const Icon = step.state === 'blocked' ? Ban : STEP_ICON[step.key];
            const tone =
              step.state === 'done'
                ? 'border-success bg-success/10 text-success'
                : step.state === 'current'
                  ? 'border-primary text-primary'
                  : step.state === 'blocked'
                    ? 'border-destructive bg-destructive/10 text-destructive'
                    : 'border-border text-muted-foreground';
            return (
              <li key={step.key} className="relative flex flex-col items-start gap-1.5">
                <div className="flex w-full items-center gap-2">
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
                      tone,
                    )}
                    aria-hidden="true"
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {i < steps.length - 1 && (
                    <span
                      className={cn(
                        'hidden h-0.5 flex-1 rounded sm:block',
                        step.state === 'done' ? 'bg-success' : 'bg-border',
                      )}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      step.state === 'todo' && 'text-muted-foreground',
                      step.state === 'blocked' && 'text-destructive',
                    )}
                  >
                    {step.state === 'blocked' ? 'Declined' : step.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stamp(step.at) ?? (step.state === 'current' ? 'Waiting' : step.state === 'blocked' ? stamp(job.tech_ack_at ?? null) ?? '' : '—')}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>

        {eta && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>
              {eta.late ? 'Running late: ' : ''}ETA about {etaStamp(eta.expectedAt.toISOString())}
            </AlertTitle>
            <AlertDescription>
              {techName} said {eta.minutes} min away at {etaStamp(eta.sentAt)}.
            </AlertDescription>
          </Alert>
        )}

        {progress?.stage === 'declined' && (
          <Alert variant="destructive">
            <Ban className="h-4 w-4" />
            <AlertTitle>{techName} declined this job</AlertTitle>
            <AlertDescription>
              {progress.declineReason ? `"${progress.declineReason}"` : 'No reason given.'} Pick {techName} again, or
              another technician, under Assigned Tech to send the job out again.
            </AlertDescription>
          </Alert>
        )}

        {progress?.holdReason && (
          <Alert>
            <PauseCircle className="h-4 w-4" />
            <AlertTitle>On hold from the field</AlertTitle>
            <AlertDescription>"{progress.holdReason}"</AlertDescription>
          </Alert>
        )}

        {progress?.awaitingReconfirm && (
          <Alert>
            <CalendarClock className="h-4 w-4" />
            <AlertTitle>Awaiting tech confirmation</AlertTitle>
            <AlertDescription>
              {techName} was asked again{progress.at ? ` on ${stamp(progress.at)}` : ''} (reschedule or re-send) and has
              not confirmed in the app yet.
            </AlertDescription>
          </Alert>
        )}

        {progress?.stage === 'not_accepted' && (
          <p className="text-sm text-muted-foreground">
            {techName} has not acknowledged this job in the app yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
